import { Component, type ReactNode, type ErrorInfo } from "react";
import { ClientLogger } from "@/lib/clientLogger";

type Props = {
  children: ReactNode;
  messageId?: string;
  diagnostics?: Record<string, unknown>;
};

type State = {
  hasError: boolean;
  error: Error | null;
};

/**
 * Per-message error boundary. Catches render crashes in assistant message content
 * (ProcessWindow, MarkdownText, Sources, etc.) and shows a graceful fallback
 * instead of crashing the entire thread.
 */
export class MessageErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    ClientLogger.error("Message render failed", {
      error,
      component: "MessageErrorBoundary",
      action: "message-render",
      metadata: {
        ...this.props.diagnostics,
        messageId: this.props.messageId,
        componentStack: info.componentStack?.slice(0, 4000),
      },
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
          <p className="font-medium">This message failed to render</p>
          <details className="mt-2">
            <summary className="cursor-pointer text-xs opacity-70">
              Technical details
            </summary>
            <pre className="mt-1 max-h-32 overflow-auto whitespace-pre-wrap text-xs opacity-60">
              {this.state.error?.message}
            </pre>
          </details>
        </div>
      );
    }
    return this.props.children;
  }
}
